/**
 * Actions relating to the current question activity being completed by a
 * student (This is the logic relating to the skill-delivery view and answering
 * questions that used to be in the old actions.js)
 */

export const SET_QUESTION_ACTIVITY = 'SET_QUESTION_ACTIVITY';
export const REMOVE_QUESTION_ACTIVITY = 'REMOVE_QUESTION_ACTIVITY';
export const SUBMIT_ANSWERS = 'SUBMIT_ANSWERS';
export const SUBMIT_LQD_ANSWERS = 'SUBMIT_LQD_ANSWERS';
export const SUBMIT_RATING = 'SUBMIT_RATING';
export const SET_ANSWERS_SUBMITTED = 'SET_ANSWERS_SUBMITTED';
export const SET_NUMERIC_ANSWER = 'SET_NUMERIC_ANSWER';
export const SET_EXPRESSION_ANSWER = 'SET_EXPRESSION_ANSWER';
export const SELECT_CARD = 'SELECT_CARD';
export const CLEAR_SELECTED_CARD = 'CLEAR_SELECTED_CARD';
export const DRAG_CARD = 'DRAG_CARD';
export const SELECT_CHOICE = 'SELECT_CHOICE';
export const TOGGLE_CHOICE = 'TOGGLE_CHOICE';
export const SET_STYLES = 'SET_STYLES';
export const RESET_STYLES = 'RESET_STYLES';
export const QUESTION_ACTIVITY_FINISHED = 'QUESTION_ACTIVITY_FINISHED';

export const UPDATE_ACTIVITY_WITH_RESULT = 'UPDATE_ACTIVITY_WITH_RESULT';
export const RESULT_PAGE_CONTINUE = 'RESULT_PAGE_CONTINUE';
export const UPDATE_ACTIVITY_WITH_SEEK_HELP = 'UPDATE_ACTIVITY_WITH_SEEK_HELP';
export const SEEK_HELP_PAGE_CONTINUE = 'SEEK_HELP_PAGE_CONTINUE';

export const PAUSE_QUESTION = 'PAUSE_QUESTION';

/**
 * Action for setting a question activity
 */
export function setQuestionActivity(attemptId, info, taskItemId, stage) {
    return { type: SET_QUESTION_ACTIVITY, attemptId, info, taskItemId, stage };
}

/**
 * Action to remove a question activity with a given attempt id
 * from the state
 * @param attemptId
 */
export function removeQuestionActivity(attemptId) {
    return { type: REMOVE_QUESTION_ACTIVITY, attemptId };
}

/**
 * Action to submit answers entered by a student for a question activity
 * @param attemptId - the ID of the attempt to submit
 * @param options is object of options such as ignoreResult and isSparxwebV2
 */
export function submitAnswers(attemptId, options = {}) {
    return { ...options, type: SUBMIT_ANSWERS, attemptId };
}

/**
 * Action to submit answers entered for a single LQD question activity.
 * @param attemptId
 */
export function submitLQDAnswers(attemptId) {
    return {
        type: SUBMIT_LQD_ANSWERS,
        attemptId: attemptId,
    };
}

/**
 * Action to submit a rating by a student for a question activity
 * @param attemptId the question attempt
 * @param confidence the confidence at the question
 * @param nextItem the next task item to select after the confidence is submitted
 */
export function submitRating(attemptId, rating, nextItem = null) {
    return { type: SUBMIT_RATING, attemptId, rating, nextItem };
}

/**
 * Action to record that answers have been submitted for a question activity
 * (to stop them being submitted more than once)
 * @param answersSubmitted if true, the answers have been submitted, false
 * if not
 */
export function setAnswersSubmitted(attemptId, answersSubmitted) {
    return { type: SET_ANSWERS_SUBMITTED, attemptId, answersSubmitted };
}

/**
 * Action to record an answer to a question activity input by the user
 * @param attemptId
 * @param stepNumber
 * @param numberFieldId
 * @param answer the numeric answer as a string
 */
export function setNumericAnswer(attemptId, stepNumber, numberFieldId, answer) {
    return { type: SET_NUMERIC_ANSWER, attemptId, stepNumber, numberFieldId, answer };
}

/**
 * Action to record an answer to a question activity input by the user
 * @param attemptId
 * @param stepNumber
 * @param numberFieldId
 * @param answer the expression answer as a asciimath string
 */
export function setExpressionAnswer(attemptId, stepNumber, numberFieldId, answer) {
    return { type: SET_EXPRESSION_ANSWER, attemptId, stepNumber, numberFieldId, answer };
}

/**
 * Action to record that the user has selected a card in a question answer
 * @param attemptId
 * @param stepNumber
 * @param cardId the id of the card
 */
export function selectCard(attemptId, stepNumber, cardId) {
    return { type: SELECT_CARD, attemptId, stepNumber, cardId };
}

/**
 * Action to clear the currently selected card
 * @param attemptId
 * @param stepNumber
 */
export function clearSelectedCard(attemptId, stepNumber) {
    return { type: CLEAR_SELECTED_CARD, attemptId, stepNumber };
}

/**
 * Action to record that the user has dragged a card into a slot in a
 * question answer
 * @param attemptId
 * @param stepNumber
 * @param slotId the id of the slot
 */
export function dragCard(attemptId, stepNumber, slotId) {
    return { type: DRAG_CARD, attemptId, stepNumber, slotId };
}

export function selectChoice(attemptId, stepNumber, choiceId, selected) {
    return { type: SELECT_CHOICE, attemptId, stepNumber, choiceId, selected };
}

/**
 * Action record that the user has toggled an answer choice option in a
 * question answer
 * @param attemptId
 * @param stepNumber
 * @param choiceId the id of the answer choice being toggled
 */
export function toggleChoice(attemptId, stepNumber, choiceId) {
    return { type: TOGGLE_CHOICE, attemptId, stepNumber, choiceId };
}

/**
 * Action to adjust the style of a question input (e.g. when the screen size
 * changes)
 * @param attemptId
 * @param stepNumber
 * @param styles
 */
export function setStyles(attemptId, stepNumber, styles) {
    return { type: SET_STYLES, attemptId, stepNumber, styles };
}

export function resetStyles(attemptId, stepNumber) {
    return { type: RESET_STYLES, attemptId, stepNumber };
}

/**
 * Action for when a question activity has been answered and the answer has
 * been processed, for example to trigger the state for the question to be
 * removed, or in the case of tethering, to restart the question activity
 * @param attemptId
 * @param questionActivity
 * @param evaluation - a string determining the evaluation of the question -
 * "correct", "wrong" or "error" - (see EVALUATIONS in question-activity.js)
 */
export function questionActivityFinished(attemptId, questionActivity, evaluation) {
    return { type: QUESTION_ACTIVITY_FINISHED, attemptId, questionActivity, evaluation };
}

export const QUESTION_RESULT = {
    // Question answered correctly.
    correct: 'correct',
    // Question answered incorrectly.
    incorrect: 'incorrect',
    // Question answered incorrectly with message about watching video.
    incorrectWarning: 'incorrect-warning',
    // Seek help/hands up screen.
    seekHelp: 'seek-help',
    // Question answered incorrectly combined with seek help message (used in LQD).
    incorrectSeekHelp: 'incorrect-seek-help',
};

export function updateActivityWithResult(attemptId, evaluation, message, image, givenAnswerXML) {
    return {
        type: UPDATE_ACTIVITY_WITH_RESULT,
        attemptId,
        evaluation,
        message,
        image,
        givenAnswerXML,
    };
}

// If cont is false then the view will not be popped after this action.
export function resultPageContinue(attemptId, cont = false) {
    return { type: RESULT_PAGE_CONTINUE, attemptId, cont };
}

export function updateActivityWithSeekHelp(attemptId) {
    return { type: UPDATE_ACTIVITY_WITH_SEEK_HELP, attemptId };
}

export function seekHelpPageContinue(attemptId) {
    return { type: SEEK_HELP_PAGE_CONTINUE, attemptId };
}

/**
 * Action to pause an attempt at a question with a given attempt ID.
 * @param attemptId
 */
export function pauseQuestion(attemptId) {
    return { type: PAUSE_QUESTION, attemptId };
}
